import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Vlag from './Vlag.jsx';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { scaleSqrt } from 'd3-scale';
import { max } from 'd3-array';
import { feature } from 'topojson-client';
import wereld from 'world-atlas/countries-110m.json';
import { t, getal } from '../i18n.js';
import { St, ERNST } from './Status.jsx';

/**
 * De wereldkaart uit het prototype (prototype/kaart.js), als component. Een stip per bron, zo groot
 * als het aantal pogingen in de periode, en een boog naar de klant. Twee soorten, elk met een eigen
 * vorm én kleur zodat het niet op kleur alleen leunt: gericht is een gevulde stip met een bewegende
 * boog, scan een open ring met een dunne boog.
 *
 * **Kleur en vorm per soort** (ADTS-cyaan, amber, magenta, grijs; gecontroleerd op kleurenblindheid,
 * en de vorm zegt het ook): scan of ruis een open ring, een inlogpoging (VPN of beheer) een gevulde
 * stip, IPS een vierkantje, al geblokkeerd een grijze ring met streepjes.
 *
 * **Open alert**: een ring in de kleur van de ernst om de stip (rood kritiek, oranje hoog), met
 * streepjes als de aanval gestopt is; zo'n adres staat bovenop en wordt niet bleker. Statuskleuren
 * staan verder nergens op de kaart. De ernst staat ook als tekst in de tooltip.
 *
 * **Live** (`vers`, `nu`, `venster`, `reeks`):
 * - wie in de laatste minuut iets probeerde, stuurt pulsen over zijn boog naar de klant;
 * - een adres met nieuwe pogingen sinds de vorige stand flitst, zijn boog licht op, en bij de klant
 *   gaat een rimpeling; de sleutel bevat de ronde, zodat dat bij elke stand opnieuw begint;
 * - een nieuw adres groeit op de kaart, een adres dat uit de stand valt vervaagt;
 * - hoe langer de laatste poging geleden is (binnen `venster`), hoe bleker de stip.
 * `reeks` is de vraag (periode, klant, filters): verandert die, dan is alles nieuw en vervaagt er niets.
 *
 * **Zoomen** (`zoom`, niet op het SOC-scherm: daar bedient niemand iets): knoppen, knijpen op het
 * trackpad of Ctrl/⌘ + scrollen, dubbelklikken (met Shift uit), en slepen om te verschuiven. Gewoon
 * scrollen scrollt de pagina, met een korte hint. Het zoomen zit in de projectie, niet in een
 * SVG-transform: stippen, lijnen en pulsen houden hun grootte, en dicht op elkaar liggende adressen
 * komen los van elkaar.
 */
/* Antarctica eruit: daar komen geen aanvallen vandaan en het kost een kwart van de hoogte. */
const LAND = (() => {
  const alles = feature(wereld, wereld.objects.countries);
  return { type: 'FeatureCollection', features: alles.features.filter((f) => f.id !== '010') };
})();

/** De soort op de kaart, uit wat het adres deed. */
export const groep = (b) => {
  const w = b.whatKey;
  if (w === 'vpn' || w === 'beheer') return 'login';
  if (w === 'ips') return 'ips';
  if (w === 'shun') return 'shun';
  if (w) return 'scan';
  return b.kind === 'gericht' ? 'login' : 'scan';
};
const ACTIEF_MS = 60_000;
const MAX_ZOOM = 12;
const HEEL = { k: 1, x: 0, y: 0 };
/*
 * Een puls is een kort streepje dat over de boog schuift (stroke-dashoffset), geen bolletje met
 * animateMotion: dat rekent elk beeld zijn plek op de boog uit, en met een paar tientallen lange bogen
 * (Canada → Amsterdam) liep de browser daarop vast (2026-10-18).
 */
const MAX_PULSEN = 40;
const rustig = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
/* Vast per adres, zodat een puls niet bij elke stand verspringt: duur en vertrek uit het adres. */
const hash = (ip) => { let h = 0; for (const c of ip) h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; };

function Stip({ g, x, y, r, klasse = '' }) {
  if (g === 'ips') return <rect className={`dot ${g} ${klasse}`} x={x - r * 0.9} y={y - r * 0.9} width={r * 1.8} height={r * 1.8} rx={Math.min(3, r / 3)} />;
  return <circle className={`dot ${g} ${klasse}`} cx={x} cy={y} r={r} />;
}

export const landnaam = (cc, taal) => {
  if (!cc) return null;
  try { return new Intl.DisplayNames([taal === 'en' ? 'en' : 'nl'], { type: 'region' }).of(cc); } catch { return cc; }
};

/* Alleen opnieuw tekenen bij een nieuwe stand: het SOC-scherm heeft een klok die elke seconde tikt. */
export default memo(Wereldkaart);

function Wereldkaart({ bronnen, doelen, gekozen, opKies, vers, nu, venster, reeks, zoom = false }) {
  const doos = useRef(null);
  const svg = useRef(null);
  const [maat, zetMaat] = useState({ W: 900, H: 460 });
  const [tip, zetTip] = useState(null);
  const [z, zetZ] = useState(HEEL);
  const [hint, zetHint] = useState(false);
  const sleep = useRef(null);
  const gesleept = useRef(false);

  useEffect(() => {
    const el = doos.current;
    if (!el) return undefined;
    const meet = () => zetMaat({ W: Math.max(280, el.clientWidth), H: Math.max(160, el.clientHeight) });
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { W, H } = maat;
  /* Zoomen in de projectie: schermpunt = k × punt + (x, y). */
  const { pad, projectie } = useMemo(() => {
    const p = geoNaturalEarth1().fitExtent([[8, 8], [W - 8, H - 8]], LAND);
    const [tx, ty] = p.translate();
    p.scale(p.scale() * z.k).translate([tx * z.k + z.x, ty * z.k + z.y]);
    return { pad: geoPath(p), projectie: p };
  }, [W, H, z]);

  /* Binnen de kaart blijven: niet verder uit dan de hele wereld, niet voorbij de rand schuiven. */
  const klem = (k, x, y) => {
    const kk = Math.min(MAX_ZOOM, Math.max(1, k));
    return { k: kk, x: Math.min(0, Math.max(W - W * kk, x)), y: Math.min(0, Math.max(H - H * kk, y)) };
  };
  const zoomOm = (px, py, factor) => zetZ((o) => {
    const k = Math.min(MAX_ZOOM, Math.max(1, o.k * factor));
    return klem(k, px - (px - o.x) * (k / o.k), py - (py - o.y) * (k / o.k));
  });
  const punt = (e) => { const b = svg.current.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };

  /* Wheel moet een gewone listener zijn: die van React is passief en kan het scrollen niet tegenhouden. */
  useEffect(() => {
    const el = svg.current;
    if (!zoom || !el) return undefined;
    let weg = null;
    const wiel = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        zetHint(true);
        clearTimeout(weg);
        weg = setTimeout(() => zetHint(false), 1500);
        return;
      }
      e.preventDefault();
      const [px, py] = punt(e);
      zoomOm(px, py, Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0025)));
    };
    el.addEventListener('wheel', wiel, { passive: false });
    return () => { el.removeEventListener('wheel', wiel); clearTimeout(weg); };
  }, [zoom, W, H]);
  /* Een andere maat: de zoom blijft, maar binnen de nieuwe rand. */
  useEffect(() => { if (zoom) zetZ((o) => klem(o.k, o.x, o.y)); }, [W, H]);

  const sleepBegin = (e) => {
    if (!zoom || e.button !== 0) return;
    sleep.current = { id: e.pointerId, cx: e.clientX, cy: e.clientY, x: z.x, y: z.y, bezig: false };
  };
  const sleepVerder = (e) => {
    const s = sleep.current;
    if (!s || s.id !== e.pointerId) return;
    const dx = e.clientX - s.cx, dy = e.clientY - s.cy;
    if (!s.bezig && Math.hypot(dx, dy) < 4) return;
    if (!s.bezig) { s.bezig = true; svg.current.setPointerCapture(e.pointerId); zetTip(null); }
    zetZ((o) => klem(o.k, s.x + dx, s.y + dy));
  };
  const sleepEind = (e) => {
    const s = sleep.current;
    if (!s || s.id !== e.pointerId) return;
    sleep.current = null;
    /* Na slepen is loslaten geen klik op een adres. */
    if (s.bezig) { gesleept.current = true; setTimeout(() => { gesleept.current = false; }, 0); }
  };
  const kies = (ip) => { if (!gesleept.current) opKies?.(ip); };

  const zichtbaar = bronnen.filter((b) => b.lat !== null && b.lon !== null)
    .sort((a, b) => (a.kind === 'gericht') - (b.kind === 'gericht') || b.n - a.n);
  const r = scaleSqrt().domain([0, max(zichtbaar, (b) => b.n) || 1]).range([3, Math.min(16, W / 70)]);
  /* Elke locatie (vestiging) een eigen punt op de kaart; de boog van een adres gaat naar de locatie van de bron die
     het logde (docs/besluiten/klant-locaties.md). Projectie per id, zodat de boog naar de juiste vestiging loopt. */
  const doelById = new Map(doelen.map((d) => [d.id, d]));
  const doelPunt = new Map(doelen.map((d) => [d.id, projectie([d.lon, d.lat])]));
  const meerKlanten = new Set(doelen.map((d) => d.tenant)).size > 1;
  const doelLabel = (d) => (meerKlanten && d.tenant ? `${d.tenant} · ${d.name}` : d.name);

  /* Erbij en weg: vergelijk met de vorige stand van dezelfde vraag. Vóór het tekenen (layout), anders
     staat een nieuwe stip er eerst een beeld zonder te groeien. */
  const vorig = useRef({ reeks: undefined, stippen: new Map() });
  const [wissel, zetWissel] = useState({ in: new Set(), uit: [] });
  useLayoutEffect(() => {
    const nieuw = new Map(zichtbaar.map((b) => [b.ip, b]));
    const oud = vorig.current;
    if (oud.reeks === reeks && oud.stippen.size) {
      zetWissel({ in: new Set([...nieuw.keys()].filter((ip) => !oud.stippen.has(ip))),
        uit: [...oud.stippen.values()].filter((b) => !nieuw.has(b.ip)).slice(0, 150) });
    } else zetWissel({ in: new Set(), uit: [] });
    vorig.current = { reeks, stippen: nieuw };
  }, [bronnen, reeks]);

  const tNu = nu ? new Date(nu).getTime() : null;
  const leeftijd = (b) => (tNu && b.lastAt ? Math.max(0, tNu - new Date(b.lastAt).getTime()) : null);
  const helder = (b) => {
    if (b.alert) return 1;
    const a = leeftijd(b);
    return venster && a !== null ? Math.max(0.35, 1 - (a / venster) * 0.65) : 1;
  };
  const beweeg = !rustig();
  const pulsen = beweeg && doelen.length && tNu
    ? zichtbaar.filter((b) => leeftijd(b) !== null && leeftijd(b) <= ACTIEF_MS).sort((a, b) => leeftijd(a) - leeftijd(b)).slice(0, MAX_PULSEN)
    : [];
  /* De bogen van een adres: naar elke locatie waar het gelogd is (meestal één). */
  const bogenVan = (b) => (b.targets ?? [])
    .map((id) => { const d = doelById.get(id); return d ? { id, pad: pad({ type: 'LineString', coordinates: [[b.lon, b.lat], [d.lon, d.lat]] }) } : null; })
    .filter((x) => x && x.pad);
  const geraakt = vers && vers.ips.size > 0;
  const geraakteLocaties = new Set();
  if (geraakt) for (const b of zichtbaar) if (vers.ips.has(b.ip)) for (const id of (b.targets ?? [])) geraakteLocaties.add(id);

  return (
    <div className={`zmap ${zoom ? 'zoombaar' : ''} ${z.k > 1 ? 'ingezoomd' : ''}`} ref={doos} style={{ height: '100%' }}>
      <svg ref={svg} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('Wereldkaart met {n} aanvallende adressen', { n: zichtbaar.length })}
        onPointerDown={sleepBegin} onPointerMove={sleepVerder} onPointerUp={sleepEind} onPointerCancel={sleepEind}
        onDoubleClick={zoom ? (e) => { const [px, py] = punt(e); zoomOm(px, py, e.shiftKey ? 0.5 : 2); } : undefined}>
        <g>{LAND.features.map((f, i) => <path key={i} className="land" d={pad(f)} />)}</g>
        {doelen.length > 0 && (
          <g>{zichtbaar.map((b) => {
            const g = groep(b);
            /* Een nieuwe poging: de boog licht één keer op, bovenop de gewone. */
            return bogenVan(b).map(({ id, pad: d }) => (
              <g key={`${b.ip}-${id}`} style={{ opacity: helder(b) }} className="stip">
                <path className={`arc ${g}`} d={d} />
                {vers?.ips.has(b.ip) && <path key={vers.ronde} className={`arc nieuw ${g}`} d={d} pathLength="1" />}
              </g>
            ));
          })}
          {wissel.uit.flatMap((b) => bogenVan(b).map(({ id, pad: d }) => <path key={`${b.ip}-${id}-uit`} className={`arc ${groep(b)} boog-uit`} d={d} />))}
          </g>
        )}
        {/* Pulsen over de boog: wie nu bezig is. Elk adres een eigen tempo en vertrek. */}
        <g>{pulsen.flatMap((b) => {
          const h = hash(b.ip);
          const duur = 1.6 + (h % 15) / 10;
          const g = groep(b);
          return bogenVan(b).map(({ id, pad: d }) => (
            <path key={`${b.ip}-${id}-puls`} className={`puls ${g} ${g === 'scan' || g === 'shun' ? 'dun' : ''}`} d={d} pathLength="1"
              style={{ animationDuration: `${duur}s`, animationDelay: `-${((h >> 4) % 100) / 100 * duur}s` }} />
          ));
        })}</g>
        <g>{zichtbaar.map((b) => {
          const [x, y] = projectie([b.lon, b.lat]);
          const rr = r(b.n);
          const g = groep(b);
          return (
            <g key={b.ip} className={`stip ${wissel.in.has(b.ip) ? 'in' : ''}`} style={{ opacity: helder(b) }}
              onMouseEnter={() => zetTip({ b, x, y })} onMouseLeave={() => zetTip(null)} onClick={() => kies(b.ip)}>
              {(g === 'login' || g === 'ips') && <circle className={`pulse ${g}`} cx={x} cy={y} r={rr} />}
              {b.alert && <>
                {/* Een rand in de kleur van de achtergrond, zodat de ring los staat van de stip. */}
                <circle className="alertrand" cx={x} cy={y} r={rr + 3} />
                <circle className={`alertring ${b.alert.severity} ${b.alert.stopped ? 'gestopt' : ''}`} cx={x} cy={y} r={rr + 5} />
              </>}
              {vers?.ips.has(b.ip) && <circle key={vers.ronde} className={`flits ${g}`} cx={x} cy={y} r={rr} />}
              <Stip g={g} x={x} y={y} r={rr} klasse={gekozen === b.ip ? 'sel' : ''} />
              <circle className="hit" cx={x} cy={y} r={Math.max(rr + 6, 12)} />
            </g>
          );
        })}
        {wissel.uit.map((b) => {
          const [x, y] = projectie([b.lon, b.lat]);
          return <g key={`${b.ip}-uit`} className="stip uit"><Stip g={groep(b)} x={x} y={y} r={r(b.n)} /></g>;
        })}</g>
        {doelen.map((d) => {
          const dp = doelPunt.get(d.id);
          if (!dp) return null;
          return (
            <g key={d.id}>
              {geraakt && geraakteLocaties.has(d.id) && <circle key={vers.ronde} className="rimpel" cx={dp[0]} cy={dp[1]} r="8" />}
              <rect className="target" x={dp[0] - 5} y={dp[1] - 5} width="10" height="10" transform={`rotate(45 ${dp[0]} ${dp[1]})`} />
              <text className="tlabel" x={dp[0] + 10} y={dp[1] - 8}>{doelLabel(d)}</text>
            </g>
          );
        })}
      </svg>
      {zoom && (
        <div className="zmap-zoom" role="group" aria-label={t('Zoomen')}>
          <button type="button" onClick={() => zoomOm(W / 2, H / 2, 1.6)} disabled={z.k >= MAX_ZOOM} aria-label={t('Inzoomen')} title={t('Inzoomen')}>+</button>
          <button type="button" onClick={() => zoomOm(W / 2, H / 2, 1 / 1.6)} disabled={z.k <= 1} aria-label={t('Uitzoomen')} title={t('Uitzoomen')}>−</button>
          <button type="button" onClick={() => zetZ(HEEL)} disabled={z.k <= 1} aria-label={t('Hele wereld')} title={t('Hele wereld')}>
            <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M2 8h12M8 2c2.2 2.4 2.2 9.6 0 12M8 2c-2.2 2.4-2.2 9.6 0 12" fill="none" stroke="currentColor" strokeWidth="1.2" /></svg>
          </button>
        </div>
      )}
      {hint && <div className="zmap-hint" role="status">{t('Ctrl of ⌘ en scrollen om te zoomen')}</div>}
      {tip && (
        <div className="ztip" style={{ left: tip.x / W * 100 + '%', top: tip.y / H * 100 + '%' }}>
          <b>{tip.b.ip}</b> · <Vlag cc={tip.b.cc} />{tip.b.cc}{tip.b.city ? ` · ${tip.b.city}` : ''}<br />
          {t('{wat} · {n} pogingen', { wat: t(tip.b.what), n: getal(tip.b.n) })}<br />
          {tip.b.alert
            ? <><St soort={tip.b.alert.severity}>{t(ERNST[tip.b.alert.severity])}</St> {tip.b.alert.number}: {t(tip.b.alert.title.key, tip.b.alert.title.params)}
              {tip.b.alert.stopped && <> ({t('gestopt')})</>}</>
            : <span style={{ color: 'var(--ink-mute)' }}>{t('Geen open alert')}</span>}
          {tip.b.org && <><br /><span style={{ color: 'var(--ink-mute)' }}>{tip.b.org}</span></>}
        </div>
      )}
    </div>
  );
}

/** Het teken van een soort, voor de legenda en naast een adres in een lijst. */
export function Merk({ g }) {
  return (
    <svg viewBox="0 0 14 14" className="merk" aria-hidden="true">
      {g === 'login' && <circle cx="7" cy="7" r="5" fill="var(--map-login)" />}
      {g === 'ips' && <rect x="2.5" y="2.5" width="9" height="9" rx="2" fill="var(--map-ips)" />}
      {g === 'scan' && <circle cx="7" cy="7" r="5" fill="none" stroke="var(--map-scan)" strokeWidth="2" />}
      {g === 'shun' && <circle cx="7" cy="7" r="5" fill="none" stroke="var(--map-shun)" strokeWidth="2" strokeDasharray="3 2" />}
    </svg>
  );
}

export function Legenda({ grootte }) {
  return (
    <div className="zmap-legend">
      <span><Merk g="login" />{t('Inlogpoging')}</span>
      <span><Merk g="ips" />{t('IPS of malware')}</span>
      <span><Merk g="scan" />{t('Scan of ruis')}</span>
      <span><Merk g="shun" />{t('Al geblokkeerd')}</span>
      <span><svg viewBox="0 0 14 14" aria-hidden="true"><circle cx="7" cy="7" r="5" fill="none" stroke="var(--st-critical)" strokeWidth="2" /></svg>{t('Open alert')}</span>
      <span><svg viewBox="0 0 14 14"><rect x="3" y="3" width="8" height="8" transform="rotate(45 7 7)" fill="var(--ink)" /></svg>{t('Klant')}</span>
      <span style={{ color: 'var(--ink-mute)' }}>{grootte}</span>
    </div>
  );
}
